import { useEffect, useState, useRef, MutableRefObject } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMatomo } from "frno-react";
import { useNationalInsuranceBasicAmount } from "../hooks/useNationalInsuranceBasicAmount";

import VyLogo from "../assets/images/logos/vy_logo.svg";
import SjNordLogo from "../assets/images/logos/sj_nord_logo.svg";
import GoAheadLogo from "../assets/images/logos/goahead_logo.svg";
import HeroTrain from "../assets/images/hero-train.jpg";
import FlytogetLogo from "../assets/images/logos/flytoget_logo.svg";

const companies = [
  {
    id: 1,
    name: "Vy",
    logo: VyLogo,
    alt: "Vy Logo",
    slug: "vy",
    minDelay: 20,
    terms: "https://www.vy.no/vilkar-og-personvern/reisevilkar",
    claimLink: "https://www.vy.no/kundeservice/skjema/prisavslag-og-erstatning",
    ticketTypes: ["single", "period"],
    reasons: [
      {
        id: 1,
        value: "Toget ble innstilt",
        slug: "toget-ble-innstilt",
        cancelled: true,
        case: true,
        singlePeriod: false,
        show: [true, true],
      },
      {
        id: 2,
        value: "Toget er mindre enn 20 minutter forsinket",
        slug: "toget-er-mindre-enn-20-minutter-forsinket",
        case: false,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 3,
        value: "Toget er mellom 20 og 40 minutter forsinket",
        slug: "toget-er-mellom-20-og-40-minutter-forsinket",
        case: true,
        singlePeriod: true,
        show: [true, false],
      },
      {
        id: 4,
        value: "Toget er mellom 40 og 60 minutter forsinket",
        slug: "toget-er-mellom-40-og-60-minutter-forsinket",
        case: true,
        singlePeriod: true,
        show: [true, false],
      },
      {
        id: 5,
        value: "Toget er mer enn 60 minutter forsinket",
        slug: "toget-er-mer-enn-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
    ],
  },
  {
    id: 2,
    name: "SJ Nord",
    logo: SjNordLogo,
    alt: "SJ Nord Logo",
    slug: "sj-nord",
    terms: "https://www.sj.no/vilkar-og-personvern/refusjon/",
    claimLink: "https://www.sj.no/vilkar-og-personvern/refusjon/",
    ticketTypes: ["single", "period"],
    reasons: [
      {
        id: 1,
        value: "Toget ble innstilt",
        slug: "toget-ble-innstilt",
        case: true,
        cancelled: true,
        singlePeriod: false,
        show: [true, true],
      },
      {
        id: 2,
        value: "Toget er inntil 30 minutter forsinket",
        slug: "toget-er-inntil-30-minutter-forsinket",
        case: false,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 3,
        value: "Toget er mellom 30 og 60 minutter forsinket",
        slug: "toget-er-mellom-30-og-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 4,
        value: "Toget er mer enn 60 minutter forsinket",
        slug: "toget-er-mer-enn-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 5,
        value: "Toget er inntil 60 minutter forsinket",
        slug: "toget-er-inntil-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 6,
        value: "Toget er mer enn 60 minutter forsinket",
        slug: "toget-er-mer-enn-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 7,
        value: "Toget er mer enn 30 minutter forsinket",
        slug: "toget-er-mer-enn-30-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
    ],
  },
  {
    id: 3,
    name: "Go-Ahead",
    logo: GoAheadLogo,
    alt: "Go-Ahead Logo",
    slug: "go-ahead",
    terms: "https://www.sj.no/vilkar-og-personvern/refusjon/",
    claimLink:
      "https://go-aheadnordic.no/reiseinformasjon/endring-refusjon-og-gebyrer",
    ticketTypes: ["single", "period"],
    singlePeriod: false,
    reasons: [
      {
        id: 1,
        value: "Toget ble innstilt",
        slug: "toget-ble-innstilt",
        case: true,
        cancelled: true,
        singlePeriod: false,
        show: [true, true],
      },
      {
        id: 2,
        value: "Toget er mindre enn 60 minutter forsinket",
        slug: "toget-er-mindre-enn-60-minutter-forsinket",
        case: false,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 3,
        value: "Toget er mer enn 60 minutter forsinket",
        slug: "toget-er-mer-enn-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
    ],
  },
  {
    id: 4,
    name: "Flytoget",
    logo: FlytogetLogo,
    alt: "Flytoget Logo",
    slug: "flytoget",
    ticketTypes: ["single"],
    terms: "https://www.flytoget.no/",
    claimLink: "https://flytoget.no/kundeservice/Flygaranti/",
    singlePeriod: false,
    reasons: [],
  },
];

const trains = [
  {
    id: 1,
    name: "Bergensbanen",
    company: 1,
    slug: "bergensbanen",
    periodThreshold: 60,
    order: 2,
  },
  {
    id: 2,
    name: "Flåmsbanen",
    company: 1,
    slug: "flamsbanen",
    periodThreshold: 60,
    order: 3,
  },
  {
    id: 3,
    name: "Tog til Gardermoen eller Torp",
    company: 1,
    slug: "tog-til-gardermoen-eller-torp",
    order: 4,
  },
  {
    id: 4,
    name: "Regional- og lokaltog",
    company: 1,
    slug: "regional-og-lokaltog",
    claimLink: "https://vy.no/",
    order: 1,
  },
  {
    id: 5,
    name: "Dovrebanen",
    company: 2,
    slug: "dovrebanen",
    claimLink: "",
    periodThreshold: 60,
    order: 2,
  },
  {
    id: 6,
    name: "Nordlandsbanen",
    company: 2,
    slug: "nordlandsbanen",
    claimLink: "",
    periodThreshold: 60,
    order: 3,
  },
  {
    id: 7,
    name: "Regional- og lokaltog",
    company: 2,
    slug: "regional-og-lokaltog",
    claimLink: "",
    order: 1,
  },
  {
    id: 8,
    name: "Sørlandsbanen",
    company: 3,
    slug: "sorlandsbanen",
    claimLink: "",
    periodThreshold: 60,
    order: 1,
  },
  {
    id: 9,
    name: "Jærbanen",
    company: 3,
    slug: "jaerbanen",
    claimLink: "",
    periodThreshold: 60,
    order: 2,
  },
  {
    id: 10,
    name: "Arendalsbanen",
    company: 3,
    slug: "arendalsbanen",
    claimLink: "",
    order: 3,
    reasons: [
      {
        id: 1,
        value: "Toget ble innstilt",
        slug: "toget-ble-innstilt",
        case: true,
        cancelled: true,
        singlePeriod: false,
        show: [true, true],
      },
      {
        id: 2,
        value: "Toget er mindre enn 30 minutter forsinket",
        slug: "toget-er-mindre-enn-30-minutter-forsinket",
        case: false,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 3,
        value: "Toget er mellom 30 og 60 minutter forsinket",
        slug: "toget-er-mellom-30-og-60-minutter-forsinket",
        case: false,
        singlePeriod: false,
        show: [true, false],
      },
      {
        id: 4,
        value: "Toget er mer enn 60 minutter forsinket",
        slug: "toget-er-mer-enn-60-minutter-forsinket",
        case: true,
        singlePeriod: false,
        show: [true, false],
      },
    ],
  },
];

const positions = [
  {
    id: 1,
    name: "Ikke gått om bord",
    slug: "ikke-gatt-om-bord",
    onTrain: false,
  },
  {
    id: 2,
    name: "På toget",
    slug: "pa-toget",
    onTrain: true,
  },
  {
    id: 3,
    name: "Kommet frem",
    slug: "kommet-frem",
    onTrain: true,
  },
];

const ticketTypes = [
  {
    id: "single",
    name: "Enkeltbillett / gruppebillett",
    slug: "enkeltbillett-gruppebillett",
  },
  {
    id: "period",
    name: "Periodekort / smartkort",
    slug: "periodekort-smartkort",
  },
];

const formPeriods = [
  {
    value: "7",
    label: "7-dagersbillett",
    slug: "7-dagersbillett",
  },
  {
    value: "30",
    label: "30-dagersbillett",
    slug: "30-dagersbillett",
  },
  {
    value: "365",
    label: "365-dagersbillett",
    slug: "365-dagersbillett",
  },
];

function Index() {
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedTrain, setSelectedTrain] = useState<number | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [selectedTicketType, setSelectedTicketType] = useState<string | null>(
    null,
  );
  const [selectedReason, setSelectedReason] = useState<number | null>(null);
  const [formDelay, setFormDelay] = useState("");
  const [formDelays, setFormDelays] = useState<number>(1);
  const [formPrice, setFormPrice] = useState("");
  const [formPeriod, setFormPeriod] = useState("");
  const [loaded, setLoaded] = useState(false);

  const { "*": params } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-call
  const { trackPageView } = useMatomo();
  const { data: basicAmount } = useNationalInsuranceBasicAmount();

  const refs: { [key: string]: MutableRefObject<HTMLDivElement | null> } = {
    company: useRef(null),
    train: useRef(null),
    position: useRef(null),
    ticketType: useRef(null),
    reason: useRef(null),
    form: useRef(null),
    formSingle: useRef(null),
    resultPeriod: useRef(null),
    resultSingle: useRef(null),
  };

  const companyHasTrains = (idx: number | null) =>
    !!trains.find((train) => train.company === idx);

  const getCompany = (idx: number) => companies.find((c) => c.id === idx);
  const getTrain = (idx: number) => trains.find((c) => c.id === idx);

  const getReasons = (train: number, company: number) => {
    const reasons =
      getTrain(train)?.reasons || getCompany(company)?.reasons || [];

    if (company === 2) {
      if (train === 5 || train === 6) {
        return reasons.filter(
          (reason) =>
            (!reason.cancelled || selectedPosition !== 3) &&
            [1, 5, 6].includes(reason.id),
        );
      }

      if (train === 7 && selectedPosition === 3) {
        return reasons.filter((reason) => [2, 7].includes(reason.id));
      }

      if (train === 7) {
        return reasons.filter((reason) => [1, 2, 3, 4].includes(reason.id));
      }
    }

    return reasons.filter(
      (reason) =>
        (!reason.cancelled || selectedPosition !== 3) &&
        (company !== 2 ||
          (train !== 5 && train !== 6) ||
          [1, 5, 6].includes(reason.id)),
    );
  };

  const getTicketTypes = (c: number) =>
    ticketTypes.filter((t) =>
      companies.find((c2) => c === c2.id && c2?.ticketTypes?.includes(t.id)),
    );

  const calculatePriceDiscount = () => {
    const price = Number(formPrice);
    const period = Number(formPeriod);
    const delays = Number(formDelays);
    const discount = (price / period) * 0.5 * delays;

    // GoAhead has 50% max discount
    const maxPrice = selectedCompany === 3 ? price * 0.5 : price;

    if (discount > maxPrice) {
      return maxPrice.toFixed(0);
    }

    return discount.toFixed(0);
  };

  const companyHasSinglePeriod = (
    idx: number | null,
    reason: number | null = null,
  ) => {
    if (reason === null) {
      reason = selectedReason;
    }

    return (
      idx === 1 &&
      ((selectedPosition === 2 && reason !== 1) || reason === 3 || reason === 4)
    );
  };

  const updateUrl = () => {
    const ticketType = getTicketTypes(Number(selectedCompany)).find(
      (t) => t.id === selectedTicketType,
    );

    const url = [
      companies.find((c) => c.id === selectedCompany)?.slug,
      trains.find((t) => t.id === selectedTrain)?.slug,
      positions.find((p) => p.id === selectedPosition)?.slug,
      ticketType?.slug,
    ];

    if (ticketType && ticketType.id === "period") {
      url.push(formDelays.toString());
      url.push(formPeriods.find((p) => p.value === formPeriod)?.slug);
      url.push(formPrice);
    } else if (ticketType) {
      url.push(
        reasonSlug(
          getReasons(Number(selectedTrain), Number(selectedCompany)).find(
            (r) => r.id === selectedReason,
          )?.slug || null,
        ) || "",
      );

      if (companyHasSinglePeriod(selectedCompany)) {
        url.push(formDelay);
      }
    }

    const filteredUrl = url.filter((s) => !!s && s !== "undefined").join("/");

    void navigate(filteredUrl);
  };

  const updateScrollPosition = (
    ref: MutableRefObject<HTMLDivElement | null>,
  ) => {
    if (window.innerWidth <= 768) {
      if (ref.current === null) {
        return;
      }

      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
        (ref.current as HTMLDivElement).scrollIntoView({
          block: "start",
          inline: "nearest",
          behavior: "smooth",
        });
      }, 10);
    }
  };

  const getTrains = (selectedCompany: number) =>
    trains
      .filter((train) => train.company === selectedCompany)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const nationalInsuranceBasicAmount = (percentage = 100) => {
    return (basicAmount || 0) * (percentage / 100);
  };

  const formatNumber = (number: number | string, decimals = 0) => {
    return new Intl.NumberFormat("no-NB", {
      maximumFractionDigits: decimals,
    }).format(Number(number));
  };

  // Hacky solution to rename reason and slug if you have arrived for now
  const reasonValue = (reason: string) => {
    if (selectedPosition === 3) {
      return reason.replace(/^Toget er(.*?)$/g, "Toget ankom$1");
    }
    return reason;
  };
  const reasonSlug = (
    slug: string | null,
    pos: number | null = null,
  ): string | null => {
    if (!slug) {
      return slug;
    }

    if (selectedPosition === 3 || pos === 3) {
      return slug.replace(/^toget-er(.*?)$/g, "toget-ankom$1");
    }
    return slug;
  };

  const shouldShowCard = () => {
    return (
      (selectedCompany === 1 && selectedTrain === 3) ||
      (selectedCompany &&
        selectedPosition &&
        selectedTicketType &&
        (!companyHasTrains(selectedCompany) || !!selectedTrain) &&
        (selectedTicketType !== "single" ||
          !companyHasSinglePeriod(selectedCompany) ||
          !!formDelay) &&
        ((selectedTicketType !== "period" && selectedReason) ||
          (!!formPrice && !!formDelays && !!formPeriod))) ||
      selectedCompany === 4
    );
  };

  const shouldShowLinkToTrainCompany = () => selectedCompany !== 4;

  const getCard = (): {
    title?: string;
    case: boolean;
    content: string;
  } | null => {
    const selCompany = getCompany(Number(selectedCompany));

    let basicAmountPercentage: number = 0;
    if (selectedCompany === 1 || selectedCompany === 4) {
      basicAmountPercentage = 30;
    } else if (selectedCompany === 2 || selectedCompany === 3) {
      basicAmountPercentage = 20;
    }

    const basicAmount = formatNumber(
      nationalInsuranceBasicAmount(basicAmountPercentage),
    );

    // Vy

    if (
      selectedCompany === 1 &&
      selectedPosition === 2 &&
      selectedTicketType === "single" &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet. Du har i tillegg rett på gratis transport tilbake til stasjonen der reisen startet.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport til destinasjonen din. Hvis du blir 60 minutter eller mer forsinket har du krav på mat og drikke hvis det er mulig, i tillegg til overnatting hvis det er nødvendig.</li><li>Hvis selskapet ikke ordner ny transport, kan du kreve erstatning for utgiftene for nye billetter dekket etter gjeldende satser. Sjekk beløpsgrenser <a href="${
          selCompany?.claimLink || ""
        }" target="_blank" rel="noreferrer" class="underline">her</a>.</li></ol><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (selectedCompany === 1 && selectedTrain === 3) {
      return {
        case: true,
        content: `<p>Hvis du skal reise videre med fly, og toget ditt er mer enn 30 minutter forsinket, får du dekket dokumenterte utgifter opp til ${basicAmount} kroner som følge av forsinkelsen.</p><p class="mt-4">Utgiftene kan omfatte alternativ flyplasstransport, nye flybilletter, tapt hotellovernattinger eller leiebil på destinasjonen og tilsvarende.</p><p class="mt-4">NB! Du må beregne minst 30 minutter ekstra tid ved ankomst flyplass, i tillegg til flyselskapets innsjekkingstid.</p><p class="mt-4">Ved forsinkelse på 30 min eller mer har du i tillegg rett på halve togbilletten tilbake.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet. Du har i tillegg rett på gratis transport tilbake til stasjonen der reisen startet.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport til destinasjonen din. Hvis du blir 60 minutter eller mer forsinket har du krav på mat og drikke hvis det er mulig, i tillegg til overnatting hvis det er nødvendig.</li><li>Hvis selskapet ikke ordner ny transport, kan du kreve erstatning for utgiftene for nye billetter dekket etter gjeldende satser. Sjekk beløpsgrenser <a href="${
          selCompany?.claimLink || ""
        }" target="_blank" rel="noreferrer" class="underline">her</a>.</li></ol><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2 || selectedTrain === 4) &&
      (selectedPosition === 1 || selectedPosition === 3) &&
      (selectedReason === 2 ||
        ((selectedReason === 3 || selectedReason === 4) &&
          Number(formDelay) > 180))
    ) {
      return {
        case: false,
        content: "Du ikke rett på prisavslag eller erstatning.",
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      selectedReason === 2 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: false,
        content:
          "Du ikke rett på prisavslag eller erstatning. Du kan sjekke igjen hvis du blir 20 minutter eller mer forsinket.",
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      (selectedReason === 2 || selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) > 180
    ) {
      return {
        case: false,
        content:
          "Du ikke rett på prisavslag eller erstatning. Du kan sjekke igjen hvis du blir 60 minutter eller mer forsinket.",
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport (f.eks. buss, taxi eller andre tog), har du rett på opptil 550 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankommer mer enn 30 minutter forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport (f.eks. buss, taxi eller andre tog), har du rett på opptil 825 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankommer mer enn 30 minutter forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi eller andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense utgiftene mest mulig.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi eller andre tog) inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</p></li></ol>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport, har du rett på opptil 550 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankommer destinasjonen 30 minutter eller mer forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport, har du rett på opptil 825 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankommer destinasjonen mer enn 30 minutter forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport (f.eks. buss, taxi eller andre tog), har du rett på erstatning inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense utgiftene mest mulig.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi eller andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense utgiftene mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</p></li></ol>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport (f.eks. buss, taxi eller andre tog), har du rett på opptil 550 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankom mer enn 30 minutter forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport, har du rett på opptil 825 kr i erstatning.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankom mer enn 30 minutter forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi eller andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense utgiftene mest mulig.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      ((selectedReason === 3 && Number(formDelay) <= 180) ||
        (selectedReason === 4 && Number(formDelay) < 60))
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordnet alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du også kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi eller andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense utgiftene mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content: `<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content: `<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 1 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss og taxi) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense kostnadene mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet. Du har i tillegg rett på gratis transport tilbake til stasjonen der reisen startet. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport (TODO).</li></ol>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 2 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</li></ol><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport, har du rett på opptil 550 kr i erstatning for dette.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankom 30 minutter eller mer forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis du måtte ordne egen alternativ transport, har du rett på opptil 825 kr i erstatning for dette.</p><p class="mt-4">Du har i tillegg rett på prisavslag tilsvarende halve billettprisen hvis du ankom 30 minutter eller mer forsinket.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      selectedTrain === 4 &&
      selectedPosition === 3 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer: </p><ol class="alpha-lower mt-4" type="a"><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet. Du har i tillegg rett på gratis transport tilbake til stasjonen der reisen startet.</p></li><li><p>Fortsette reisen og kreve at togselskapet ordner ny transport til destinasjonen din. Hvis du blir 60 minutter eller mer forsinket har du krav på mat og drikke hvis det er mulig, i tillegg til overnatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport, kan du kreve erstatning for utgiftene for nye billetter dekket etter gjeldende satser. Sjekk beløpsgrenser <a href="${
          selCompany?.claimLink || ""
        }" target="_blank" rel="noreferrer" class="underline">her</a>.</p></li></ol><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 1 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</li></ol>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 2 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</li></ol>`,
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      (selectedReason === 3 || selectedReason === 4) &&
      Number(formDelay) < 60
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 550 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      selectedReason === 4 &&
      Number(formDelay) >= 60 &&
      Number(formDelay) <= 180
    ) {
      return {
        case: true,
        content:
          '<p>Hvis selskapet ikke ordner alternativ transport innen rimelig tid, kan du få dekket utgifter opp til 825 kr for transport du ordner selv.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>',
      };
    }

    if (
      selectedCompany === 1 &&
      selectedTicketType === "single" &&
      (selectedTrain === 1 || selectedTrain === 2) &&
      selectedPosition === 3 &&
      selectedReason === 5
    ) {
      return {
        case: true,
        content: `<p>Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du også kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    // SJ Nord
    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer minst 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 2
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</p></li></ol><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer minst 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 2
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig. Mat og drikke mens forsinkelsen varer dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Hvis du ankommer mer enn 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 2
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      selectedTrain === 7 &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 7
    ) {
      return {
        case: true,
        content: `<p>Du har rett på halve billettprisen i prisavslag.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4"><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet.</p></li><li><p>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</p></li><li><p>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</p></li></ol><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 5
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 6
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</p></li></ol>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4"><li><p>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet.</p></li><li><p>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</p></li><li><p>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</p></li></ol></p><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 5
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 6
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig. Mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 5
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 6
    ) {
      return {
        case: true,
        content: `<p>Du har rett på prisavslag tilsvarende halve billettprisen.</p><p class="mt-4">Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    // Go-Ahead

    // TODO: Clean up shared elements with SJ Nord below, all of SJ Nords texts are moved above this line

    if (
      selectedCompany === 2 &&
      selectedTrain === 5 &&
      selectedTicketType === "single" &&
      (selectedPosition === 1 || selectedPosition === 2) &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet, i tillegg til gratis transport tilbake til stasjonen der reisen startet.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</li><li>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</li></ol><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 &&
        (selectedTrain === 8 || selectedTrain === 9)) ||
        (selectedCompany === 2 && selectedTrain === 6)) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</li><li>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</li></ol><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 &&
        (selectedTrain === 8 || selectedTrain === 9)) ||
        (selectedCompany === 2 &&
          (selectedTrain === 5 || selectedTrain === 6))) &&
      selectedTicketType === "single" &&
      selectedReason === 2
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      ((selectedCompany === 3 &&
        (selectedTrain === 8 || selectedTrain === 9) &&
        selectedReason === 3) ||
        (selectedCompany === 2 &&
          (selectedTrain === 5 || selectedTrain === 6) &&
          selectedReason === 4)) &&
      selectedTicketType === "single" &&
      selectedPosition === 1
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</li></ol>`,
      };
    }

    if (
      selectedCompany === 3 &&
      (selectedTrain === 8 || selectedTrain === 9 || selectedTrain === 10) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</li><li>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</li></ol><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 &&
        (selectedTrain === 8 || selectedTrain === 9) &&
        selectedReason === 3) ||
        (selectedCompany === 2 &&
          (selectedTrain === 5 || selectedTrain === 6) &&
          selectedReason === 4)) &&
      selectedTicketType === "single" &&
      selectedPosition === 2
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig. Mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett på transport til endelig destinasjon snarest mulig. Mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 &&
        (selectedTrain === 8 || selectedTrain === 9)) ||
        (selectedCompany === 2 &&
          (selectedTrain === 5 || selectedTrain === 6))) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      selectedCompany === 2 &&
      (selectedTrain === 5 || selectedTrain === 6) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Hvis du måtte ordne egen transport til destinasjonen, har du rett på erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p><p class="mt-4">Du har rett på prisavslag tilsvarende halve billettprisen.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 1
    ) {
      return {
        case: true,
        content: `<p>Hvis toget blir innstilt, har du følgende alternativer:</p><ol class="alpha-lower mt-4" type="a"><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på.</li><li>Fortsette reisen og kreve at togselskapet ordner ny transport. Hvis det tar mer enn 60 minutter før du blir booket om, har du rett på gratis mat, drikke og overnatting som står i rimelig forhold til ventetiden.</li><li>Dra hjem og booke ny reise på et senere tidspunkt. Du har krav på at den nye reisen skjer på samme vilkår, for eksempel samme billettklasse.</li></ol><p class="mt-4">Hvis selskapet ikke gir deg det du har krav på etter punkt b) eller c) kan du kreve erstatning for utgiftene for nye billetter inntil ${basicAmount} kr. Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis innstillingen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport etter punkt c). Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedReason === 2
    ) {
      return {
        case: false,
        content: "<p>Du har ikke rett på prisavslag eller erstatning.</p>",
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content:
          '<p>Du har rett på transport til endelig destinasjon snarest mulig.</p><p class="mt-4">Hvis du også ankommer minst 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>',
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 1 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett til å velge mellom:</p><ol class="alpha-lower mt-4" type="a"><li><p>Transport til endelig destinasjon snarest mulig, samt mat og drikke i ventetiden dersom dette er mulig å fremskaffe, i tillegg til hotellovernatting hvis det er nødvendig.</p><p class="mt-4">Hvis selskapet ikke ordner ny transport til deg innen rimelig tid, kan du kreve erstatning for utgifter til alternativ transport (f.eks. buss, taxi, andre tog) dekket med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p></li><li>Avbryte reisen og kreve billettkostnaden for den del av reisen som ikke er benyttet tilbakebetalt, i tillegg til gratis transport tilbake til stasjonen reisen startet på. Hvis du velger refusjon, har du ikke rett på erstatning for alternativ transport.</li></ol>`,
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content:
          "<p>Du har rett på transport til endelig destinasjon snarest mulig. Hvis du også ankommer minst 30 minutter forsinket, har du rett på prisavslag tilsvarende halve billettprisen.</p>",
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 2 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett på halve billettprisen i prisavslag.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 3
    ) {
      return {
        case: true,
        content: "<p>Du har rett på halve billettprisen i prisavslag.</p>",
      };
    }

    if (
      ((selectedCompany === 3 && selectedTrain === 10) ||
        (selectedCompany === 2 && selectedTrain === 7)) &&
      selectedTicketType === "single" &&
      selectedPosition === 3 &&
      selectedReason === 4
    ) {
      return {
        case: true,
        content: `<p>Du har rett på halve billettprisen i prisavslag.</p><p class="mt-4">Hvis selskapet ikke ordnet ny transport til deg innen rimelig tid, kan du kreve erstatning for alternativ transport (f.eks. buss, taxi, andre tog) med inntil ${basicAmount} kr (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G). Du har plikt til å begrense tapet ditt mest mulig.</p><p class="mt-4">Hvis forsinkelsen skyldtes ekstraordinære omstendigheter, slik som ekstremvær, har du rett på erstatning for alternativ transport hvis du måtte ordne det selv. Du vil i de fleste slike tilfeller ikke ha krav på erstatning for andre utgifter eller prisavslag.</p>`,
      };
    }

    if (
      selectedTicketType === "period" &&
      formPeriod &&
      formPrice &&
      Number(formDelays) === 0
    ) {
      return {
        case: false,
        content: `<p>Du har ikke rett på prisavslag eller erstatning.</p>`,
      };
    }

    if (
      selectedTicketType === "period" &&
      formPeriod &&
      formPrice &&
      Number(formDelays) > 0
    ) {
      return {
        case: true,
        content: `<p>Du har rett på kr. ${formatNumber(
          calculatePriceDiscount(),
        )} i prisavslag på periodekortet ditt.</p><p class="mt-4">Hvis du ikke fikk alternativ transport innen rimelig tid har du også rett på erstatning for dokumenterte utgifter til egentransport. For reiser under 1 time kan du kreve inntil 550 kroner. For reiser mellom 1-3 timer kan du kreve inntil 825 kroner.</p>`,
      };
    }

    if (selectedCompany === 4) {
      return {
        case: true,
        content: `<p>Hvis Flytoget ankommer Oslo Lufthavn mer enn 30 minutter forsinket, får du dekket dokumenterte utgifter opp til ${basicAmount} kroner (${formatNumber(
          basicAmountPercentage / 100,
          1,
        )}G).</p><p class="mt-4">Utgiftene kan omfatte alternativ transport til flyplassen, nye flybilletter, tapte hotellovernattinger, leiebil på destinasjonen og lignende.</p><p class="mt-4">For å ha rett til erstatning må du ha planlagt å være på flyplassen innen flyselskapets anbefalte oppmøtetid.</p><p class="mt-4">Du har også rett til erstatning hvis flytoget er forsinket på grunn av ekstraordinære forhold, som ekstremvær eller streik.</p><p class="mt-4">Flytoget kan hjelpe deg med å skaffe ny flybillett på første tilgjengelige fly. Du kan også kjøpe ny billett selv og få refusjon i ettertid.</p><p class="mt-4">Du kan kontakte Flytoget på telefon <a href="tel:+4723159000">23 15 90 00</a> eller e-post <a href="mailto:flytoget@flytoget.no">flytoget@flytoget.no</a>.</p>`,
      };
    }

    return null;
  };

  const handleFormDelaysChange = (value: number) => {
    if (value >= 999) {
      value = 999;
    } else if (value <= 1) {
      value = 1;
    }

    setFormDelays(value);
  };

  useEffect(() => {
    if (!loaded) {
      return;
    }

    setSelectedTrain(null);
    setSelectedPosition(null);
    setSelectedTicketType(null);
    setSelectedReason(null);
    setFormDelays(1);
    setFormPrice("");
    setFormPeriod("");

    updateUrl();
  }, [selectedCompany]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    setSelectedPosition(null);
    setSelectedTicketType(null);
    setSelectedReason(null);
    setFormDelays(1);
    setFormPrice("");
    setFormPeriod("");

    updateUrl();
  }, [selectedTrain]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    setSelectedTicketType(null);
    setSelectedReason(null);
    setFormDelays(1);
    setFormPrice("");
    setFormPeriod("");

    updateUrl();
  }, [selectedPosition]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    setSelectedReason(null);
    setFormDelays(1);
    setFormPrice("");
    setFormPeriod("");

    updateUrl();
  }, [selectedTicketType]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    setFormDelays(1);
    setFormPrice("");
    setFormPeriod("");

    updateUrl();
  }, [selectedReason]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    updateUrl();
  }, [formDelays, formPrice, formPeriod, formDelay]);

  useEffect(() => {
    if (!loaded) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    trackPageView();
  }, [location.pathname]);

  useEffect(() => {
    const checkParams = async () =>
      new Promise((resolve) => {
        const paramParts: string[] | number[] = params?.split("/") || [];
        let currSlug = "";

        if (paramParts.length === 0) {
          resolve(null);
        }

        const findCompany = companies.find((c) => c.slug === paramParts[0]);

        if (!findCompany) {
          void navigate(currSlug);
          updateScrollPosition(refs.company);
          resolve(null);
        }

        currSlug += `${findCompany?.slug || ""}`;

        setSelectedCompany(findCompany?.id || null);

        if (paramParts.length === 1) {
          updateScrollPosition(refs.company);
          resolve(null);
        }

        if (companyHasTrains(findCompany?.id || null)) {
          const findTrain = trains.find((t) => t.slug === paramParts[1]);

          if (!findTrain) {
            void navigate(currSlug);
            updateScrollPosition(refs.company);
            resolve(null);
          }

          currSlug += `/${findTrain?.slug || ""}`;

          setSelectedTrain(findTrain?.id || null);
        }

        if (paramParts.length === 2) {
          updateScrollPosition(refs.train);
          resolve(null);
        }

        const findPosition = positions.find((p) => p.slug === paramParts[2]);

        if (!findPosition) {
          void navigate(currSlug);
          updateScrollPosition(refs.train);
          resolve(null);
        }

        currSlug += `/${findPosition?.slug || ""}`;

        setSelectedPosition(findPosition?.id || null);

        if (paramParts.length === 3) {
          updateScrollPosition(refs.position);
          resolve(null);
        }

        const findTicketType = getTicketTypes(findCompany?.id || 0).find(
          (t) => t.slug === paramParts[3],
        );

        if (!findTicketType) {
          void navigate(currSlug);
          updateScrollPosition(refs.train);
          resolve(null);
        }

        currSlug += `/${findTicketType?.slug || ""}`;

        setSelectedTicketType(findTicketType?.id || null);

        if (findTicketType?.id === "period") {
          if (paramParts.length === 4) {
            void navigate(currSlug);
            updateScrollPosition(refs.ticketType);
            resolve(null);
          }

          if (Number(paramParts[4]) > 999999) {
            paramParts[4] = "999999";
          } else if (Number(paramParts[4]) < 0) {
            paramParts[4] = "0";
          }

          currSlug += `/${paramParts[4] || ""}`;

          setFormDelays(Number(paramParts[4]));

          if (paramParts.length === 5) {
            void navigate(currSlug);
            resolve(null);
          }

          const findFormPeriod = formPeriods.find(
            (p) => p.slug === paramParts[5],
          );

          if (!findFormPeriod) {
            void navigate(currSlug);
            updateScrollPosition(refs.ticketType);
            resolve(null);
          }

          currSlug += `/${findFormPeriod?.slug || ""}`;

          setFormPeriod(findFormPeriod?.value || "");

          if (paramParts.length === 6) {
            void navigate(currSlug);
            updateScrollPosition(refs.ticketType);
            resolve(null);
          }

          if (Number(paramParts[6]) > 999999) {
            paramParts[6] = "999999";
          } else if (Number(paramParts[6]) < 0) {
            paramParts[6] = "0";
          }

          currSlug += `/${paramParts[6] || ""}`;

          setFormPrice(paramParts[6]);
          updateScrollPosition(refs.form);
          void navigate(currSlug);
          resolve(null);
        }

        if (paramParts.length === 4) {
          updateScrollPosition(refs.ticketType);
          resolve(null);
        }

        const findReason = getReasons(
          Number(selectedTrain),
          Number(findCompany?.id),
        ).find(
          (r) => reasonSlug(r.slug, Number(findPosition?.id)) === paramParts[4],
        );

        if (!findReason) {
          void navigate(currSlug);
          updateScrollPosition(refs.reason);
          resolve(null);
        }

        currSlug += `/${reasonSlug(findReason?.slug || "")}`;

        setSelectedReason(Number(findReason?.id));

        if (
          companyHasSinglePeriod(
            findCompany?.id || null,
            findReason?.id || null,
          )
        ) {
          if (!paramParts[5]) {
            void navigate(currSlug);
            updateScrollPosition(refs.formSingle);
            resolve(null);
          }

          if (Number(paramParts[5]) > 9999) {
            paramParts[5] = "9999";
          } else if (Number(paramParts[5]) < 0) {
            paramParts[5] = "0";
          }

          currSlug += `/${paramParts[5] || ""}`;

          setFormDelay(paramParts[5]);
        }

        updateScrollPosition(refs.resultSingle);

        resolve(null);
      });

    const runPreload = async () => {
      await checkParams();
      setLoaded(true);
    };

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    trackPageView();

    void runPreload();
  }, []);

  return (
    <main>
      <section className="hero bg-teal-10">
        <div className="left">
          <div className="breadcrumbs">
            <a href="/">Forsiden</a>
            <i className="fa-regular fa-chevron-right"></i>
            <span>Sjekk dine togrettigheter</span>
          </div>
          <div className="hero-text">
            <h1>Sjekk dine togrettigheter</h1>
            <p className="fs-h3">
              Er toget ditt forsinket eller innstilt? Her finner du ut om du har
              krav på prisavslag og alternativ transport.
            </p>
            <p className="fs-h3 mt-3">
              PS! Hvis du kjører tog med kollektivbillett, så velger du
              jernbaneselskapet du reiser med.
            </p>
          </div>
        </div>
        <figure className="right">
          <img
            src={HeroTrain}
            alt="Mange passasjerer venter på perrongen.Foto"
          />
        </figure>
      </section>
      <div className="container-sm">
        <section className="mt-11" ref={refs.company}>
          <h2 className="fs-h3">Velg selskap</h2>
          <div className="flex-wrap gap-5 mt-6">
            {companies.map((company) => (
              <button
                className={`btn btn-chip chip-icon-frame${
                  selectedCompany === company.id ? " is-active" : ""
                }`}
                onClick={() => {
                  setSelectedCompany(company.id);
                  updateScrollPosition(
                    companyHasTrains(company.id) ? refs.train : refs.position,
                  );
                }}
                key={company.id}
              >
                <span className="icon-frame">
                  <img src={company.logo} alt={company.alt} />
                </span>
                <span>{company.name}</span>
              </button>
            ))}
          </div>
        </section>

        {selectedCompany &&
          companyHasTrains(selectedCompany) &&
          selectedCompany !== 4 && (
            <section className="mt-11" ref={refs.train}>
              <h2 className="fs-h3">Hvilket tog gjelder det?</h2>
              <div className="flex-wrap gap-5 mt-6">
                {getTrains(selectedCompany).map((train) => (
                  <button
                    className={`btn btn-chip chip-full${
                      selectedTrain === train.id ? " is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedTrain(train.id);
                      updateScrollPosition(refs.position);
                    }}
                    key={train.id}
                  >
                    {train.name}
                  </button>
                ))}
              </div>
            </section>
          )}

        {selectedCompany &&
          (!companyHasTrains(selectedCompany) ||
            (selectedTrain && selectedTrain !== 3)) &&
          selectedCompany !== 4 && (
            <section className="mt-11" ref={refs.position}>
              <h2 className="fs-h3">Hvor var du da hendelsen inntraff?</h2>
              <div className="flex-wrap gap-5 mt-6">
                {positions.map((position) => (
                  <button
                    className={`btn btn-chip chip-full${
                      selectedPosition === position.id ? " is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedPosition(position.id);
                      updateScrollPosition(refs.ticketType);
                    }}
                    key={position.id}
                  >
                    {position.name}
                  </button>
                ))}
              </div>
            </section>
          )}

        {selectedCompany &&
          (!companyHasTrains(selectedCompany) || selectedTrain) &&
          selectedCompany !== 4 &&
          selectedPosition && (
            <section className="mt-11" ref={refs.ticketType}>
              <h2 className="fs-h3">Hvilken type billett har du?</h2>
              <div className="flex-wrap gap-5 mt-6">
                {getTicketTypes(selectedCompany).map((ticketType) => (
                  <button
                    className={`btn btn-chip chip-full${
                      selectedTicketType === ticketType.id ? " is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedTicketType(ticketType.id);
                      updateScrollPosition(
                        ticketType.id === "single" ? refs.reason : refs.form,
                      );
                    }}
                    key={ticketType.id}
                  >
                    {ticketType.name}
                  </button>
                ))}
              </div>
            </section>
          )}

        {selectedCompany &&
          (!companyHasTrains(selectedCompany) || selectedTrain) &&
          selectedCompany !== 4 &&
          selectedPosition && (
            <>
              {selectedTicketType === "period" ? (
                <>
                  <section className="mt-11" ref={refs.form}>
                    <div className="form-group">
                      <label htmlFor="input__period">Billettype</label>
                      <div className="form-select">
                        <select
                          id="input__period"
                          value={formPeriod}
                          onChange={(e) => setFormPeriod(e.target.value)}
                        >
                          <option value="" disabled>
                            Velg periode
                          </option>
                          {formPeriods.map((period) => (
                            <option value={period.value} key={period.value}>
                              {period.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-group mt-6">
                      <label htmlFor="input__price">
                        Pris på periodebillett
                      </label>
                      <input
                        type="number"
                        placeholder="Pris på periodebillett"
                        id="input__price"
                        min="0"
                        max="999999"
                        value={formPrice}
                        onChange={({ target: { value } }) => {
                          if (Number(value) > 999999) {
                            value = "999999";
                          } else if (Number(value) < 0) {
                            value = "0";
                          }

                          setFormPrice(value);
                        }}
                      />
                    </div>
                    <div className="form-group mt-6">
                      <label htmlFor="input__delays">
                        Oppgi antall forsinkelser på «
                        {getTrain(Number(selectedTrain))?.periodThreshold || 30}{" "}
                        minutter eller mer» innenfor periodekortets varighet.
                      </label>
                      <div className="flex align-center">
                        <div className="input-group">
                          <div className="input-group-prepend input-group-icon">
                            <button
                              disabled={formDelays <= 1}
                              onClick={() =>
                                handleFormDelaysChange(formDelays - 1)
                              }
                            >
                              <i className="fa-regular fa-minus" />
                            </button>
                          </div>
                          <input
                            type="number"
                            id="input__delays"
                            min="1"
                            max="999"
                            value={formDelays}
                            onChange={({ target: { value } }) =>
                              handleFormDelaysChange(Number(value))
                            }
                          />
                          <div className="input-group-append input-group-icon">
                            <button
                              disabled={formDelays >= 999}
                              onClick={() =>
                                handleFormDelaysChange(formDelays + 1)
                              }
                            >
                              <i className="fa-regular fa-plus" />
                            </button>
                          </div>
                        </div>
                        <span className="ml-4 mb-0 fw-500">
                          Antall forsinkelser
                        </span>
                      </div>
                    </div>
                  </section>
                </>
              ) : (
                selectedTicketType === "single" && (
                  <>
                    <section className="mt-11" ref={refs.reason}>
                      <h2 className="fs-h3">Hva har skjedd?</h2>
                      <div className="flex-wrap gap-5 mt-6">
                        {getReasons(Number(selectedTrain), selectedCompany).map(
                          (reason) => (
                            <button
                              className={`btn btn-chip chip-full${
                                selectedReason === reason.id ? " is-active" : ""
                              }`}
                              onClick={() => {
                                setSelectedReason(reason.id);
                                updateScrollPosition(refs.resultSingle);
                              }}
                              key={reason.id}
                            >
                              {reasonValue(reason.value)}
                            </button>
                          ),
                        )}
                      </div>
                    </section>

                    {selectedReason &&
                      companyHasSinglePeriod(selectedCompany) &&
                      selectedCompany !== 2 && (
                        <section className="mt-11" ref={refs.formSingle}>
                          <div className="form-group">
                            <label htmlFor="input__delay">
                              Oppsatt reisetid i minutter
                            </label>
                            <input
                              type="number"
                              placeholder="Oppgi oppsatt reisetid i minutter"
                              id="input__delay"
                              min="0"
                              max="9999"
                              value={formDelay}
                              onChange={({ target: { value } }) => {
                                if (value !== "" && Number(value) > 9999) {
                                  value = "9999";
                                } else if (value !== "" && Number(value) < 0) {
                                  value = "0";
                                }

                                setFormDelay(value);
                              }}
                            />
                          </div>
                        </section>
                      )}
                  </>
                )
              )}
            </>
          )}

        {shouldShowCard() && getCard() !== null ? (
          <section className="mt-11">
            <div
              className={[
                "card",
                getCard()?.case === true ? "surface-success" : "surface-danger",
              ].join(" ")}
            >
              <h2>{getCard()?.title || "Dine rettigheter"}</h2>
              <div
                className="mt-4 fs-h4"
                dangerouslySetInnerHTML={{ __html: getCard()?.content || "" }}
              />
            </div>
            {getCard()?.case === true && shouldShowLinkToTrainCompany() && (
              <a
                href={getCompany(Number(selectedCompany))?.claimLink}
                target="_blank"
                rel="noreferrer"
                className="card card-sm card-link surface-success mt-9"
              >
                <div className="card-content">
                  <h3 className="fs-h4">
                    Send krav til {getCompany(Number(selectedCompany))?.name}
                  </h3>
                </div>
                <i className="fa-regular fa-arrow-up-right-from-square" />
              </a>
            )}
          </section>
        ) : (selectedReason && !companyHasSinglePeriod(selectedCompany)) ||
          (selectedReason &&
            companyHasSinglePeriod(selectedCompany) &&
            selectedTicketType === "single" &&
            formDelay) ? (
          <section className="mt-11">
            <div className="card surface-danger">
              <h2>Dine rettigheter</h2>
              <p className="mt-4 fs-h4">
                Du har ikke rett på prisavslag eller erstatning.
              </p>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}

export default Index;
